/**
 * In dev the base is empty so requests hit Vite's /v1 proxy — one origin, and the
 * session cookie "just works". In production the SPA is static on CloudFront and
 * calls api.<domain> cross-origin.
 */
const BASE = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL ?? '')
const MOCK = import.meta.env.DEV && import.meta.env.VITE_MOCK === 'true'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem,
  ) {
    super(problem.detail || problem.title || `HTTP ${status}`)
    this.name = 'ApiError'
  }
}

/** RFC 9457 problem+json, which the API returns for every failure. */
export interface Problem {
  type?: string
  title: string
  status: number
  detail?: string
  errors?: Array<{ field: string; message: string }>
}

/**
 * Single fetch wrapper for the whole SPA.
 *
 * `credentials: 'include'` is not optional: the session is an httpOnly cookie
 * scoped to the shared parent domain (.shipdesk.in), never a JWT in
 * localStorage. This dashboard renders merchant-controlled strings — order
 * notes, customer names, product titles — so any XSS would turn a readable
 * token into full account takeover.
 */
/**
 * Set by RequireAuth while a super admin is impersonating. The server rejects
 * writes then too; this is a backstop so a control the UI forgot to disable
 * fails fast and loudly in dev instead of round-tripping to a 403.
 */
let readOnly = false
export const setReadOnly = (v: boolean) => {
  readOnly = v
}
/** Writes allowed while read-only — the api's own allowlist: leaving the impersonation, or signing out. */
const READ_ONLY_ALLOWED = [/^\/auth\/logout$/, /^\/admin\/impersonate\/stop$/]

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()
  if (readOnly && method !== 'GET' && !READ_ONLY_ALLOWED.some((re) => re.test(path))) {
    if (import.meta.env.DEV) console.warn(`[read-only] blocked ${method} ${path} — this control should be disabled while impersonating`)
    throw new ApiError(403, { title: 'Read-only', status: 403, detail: 'Read-only while viewing as platform admin.' })
  }
  const url = `${BASE}/v1${path}`
  const req: RequestInit = {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  }

  const res = MOCK
    ? await (await import('../mocks/server')).mockFetch(url, req)
    : await fetch(url, req)

  if (res.status === 204) return undefined as T

  const body = await res.json().catch(() => null)

  if (!res.ok) {
    throw new ApiError(res.status, body ?? { title: res.statusText, status: res.status })
  }

  return body as T
}

const json = (data: unknown) => (data === undefined ? undefined : JSON.stringify(data))

export const get = <T>(path: string): Promise<T> => api<T>(path)

export const post = <T>(path: string, data?: unknown, init: RequestInit = {}): Promise<T> =>
  api<T>(path, { ...init, method: 'POST', body: json(data) })

export const put = <T>(path: string, data: unknown): Promise<T> =>
  api<T>(path, { method: 'PUT', body: json(data) })

export const patch = <T>(path: string, data: unknown): Promise<T> =>
  api<T>(path, { method: 'PATCH', body: json(data) })

export const del = <T>(path: string): Promise<T> => api<T>(path, { method: 'DELETE' })

/** Build `?a=1&b=2`, dropping empty values so URLs stay short and shareable. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

/** Cursor pagination envelope used by every list endpoint. */
export interface Page<T> {
  data: T[]
  page: { next_cursor: string | null; has_more: boolean }
}

export const isApiError = (e: unknown, status?: number): e is ApiError =>
  e instanceof ApiError && (status === undefined || e.status === status)

/** problem+json `errors[]` keyed by field, for inline form errors. */
export function fieldErrors(e: unknown): Record<string, string> {
  if (!(e instanceof ApiError)) return {}
  return Object.fromEntries((e.problem.errors ?? []).map((x) => [x.field, x.message]))
}

/** One human sentence for a failed request. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.problem.detail ?? e.problem.title
  if (e instanceof TypeError) return 'Network error — check your connection.'
  return 'Something went wrong.'
}

/** An OAuth hand-off URL is followed only if it's https (Shopify) or our own origin. */
export function isSafeRedirect(url: string): boolean {
  try {
    const u = new URL(url, window.location.origin)
    return u.protocol === 'https:' || u.origin === window.location.origin
  } catch {
    return false
  }
}
